<template>
  <OrganizationDashboard v-if="organizationDomain === null" />
  <UserDashboard v-else-if="dashboardConfig" :config="dashboardConfig" />
  <HubDashboard v-else />
</template>

<script setup lang="ts">
import { definePageMeta, navigateTo, useOrganizationDomain } from '#imports';
import { useNuxtApp } from 'nuxt/app';
import { useAuthUserStore } from '~/store/auth-user';
import { useOrganizationStore } from '~/store/organization';
import {
  defineNuxtRouteMiddleware,
  callWithNuxt,
  RouteMiddleware,
  useAsyncData,
} from '#app';
import { OrganizationDashboard } from '~/pages/_components/organization-dashboard';
import { HubDashboard } from '~/pages/_components/hub-dashboard';
import {
  shouldFetchUserDashboard,
  UserDashboard,
} from '~/pages/_components/user-dashboard';
import { apiGetUserDashboard } from '~/api/dashboard';
import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';

definePageMeta({
  // eslint-disable-next-line require-await
  middleware: defineNuxtRouteMiddleware(async () => {
    const nuxtApp = useNuxtApp();
    const authUserStore = useAuthUserStore();
    const organizationStore = useOrganizationStore();

    if (!authUserStore.isLoggedIn && !organizationStore.isPublic) {
      return callWithNuxt(nuxtApp, navigateTo, ['/auth/login']);
    }
  }),
});

const organizationDomain = useOrganizationDomain();
const organizationStore = useOrganizationStore();

const { data: dashboardResponse, refresh: refreshDashboard } =
  await useAsyncData('user-dashboard', () => {
    if (
      !shouldFetchUserDashboard(
        organizationDomain,
        organizationStore.organization,
      )
    ) {
      return Promise.resolve(null);
    }
    return apiGetUserDashboard();
  });

// The dashboard endpoint is public, so a server render that ran without the
// auth service worker's token comes back for an anonymous viewer (no module
// sections, public hubs only) and hydration reuses that payload. Refetch on the
// client as soon as we know the user is actually logged in.
if (import.meta.client) {
  const authUserStore = useAuthUserStore();
  const { isLoggedIn } = storeToRefs(authUserStore);
  watch(
    isLoggedIn,
    (loggedIn) => {
      if (loggedIn && dashboardResponse.value?.viewer === 'anonymous') {
        refreshDashboard();
      }
    },
    { immediate: true },
  );
}

const dashboardConfig = computed(() => dashboardResponse.value?.config ?? null);
</script>
